import { useAuthStore } from "../stores/auth.js";

import type {
  Network,
  NetworkStats,
  Transaction,
  TxStatus,
  TxExecutionResult,
  TxFacet,
  TxFacetsResponse,
  TxFilterStatsResponse,
  TxInsightsResponse,
  TxInsightsCluster,
  TxInsightsFeePoint,
  TxFilterDimension,
  TxFilterDimensionKey,
  ClusterRun,
  ClusterSize,
  UmapCluster,
  UmapOutlier,
  UmapData,
  ClusterMember,
  OutlierEntry,
  ContractLabel,
  PublicCall,
  NoteHash,
  Nullifier,
  ResolvedContract,
  PublicDataWrite,
  NearbyWrite,
  SlotSummary,
  TxSlotActivityData,
  SimilarTx,
  L2ToL1Message,
  PrivateLogDetail,
  ContractClassLogDetail,
  PublicLogDetail,
  PublicAddress,
  FeePayerStat,
  PrivacySet,
  FeePricingData,
  TxDetail,
  MurderBoardTx,
  MurderBoardCluster,
  MurderBoardFpc,
  PrivacyScoreFactor,
  MurderBoardData,
  AddressInteraction,
  AddressInteractionsResponse,
  AddressAppearance,
  RepeatedSlot,
  RepeatedSlotsResponse,
  CoTxsResponse,
  CoTxsRow,
  FeatureWeights,
  NormalizationMode,
  AnalysisConfig,
  FeatureStat,
  FeatureStatsData,
  FeeHistoryPoint,
  FeeSpreadBucket,
  CurrentFees,
  ManaStatsResponse,
  BlockHistoryPoint,
  BlockStatsData,
  BlockConfigData,
  ChainReorg,
  CheckpointStatsData,
  ChainHealth,
  ProductionStatsResponse,
  BlockDetail,
  TxCompareResponse,
  StorageOverviewData,
  SearchResponse,
  SearchInterpretation,
  SearchAddressRole,
  SearchQueryType,
} from "@clustec/common";

export type {
  Network,
  NetworkStats,
  Transaction,
  TxStatus,
  TxExecutionResult,
  TxFacet,
  TxFacetsResponse,
  TxFilterStatsResponse,
  TxInsightsResponse,
  TxInsightsCluster,
  TxInsightsFeePoint,
  TxFilterDimension,
  TxFilterDimensionKey,
  ClusterRun,
  ClusterSize,
  UmapCluster,
  UmapOutlier,
  UmapData,
  ClusterMember,
  OutlierEntry,
  ContractLabel,
  PublicCall,
  NoteHash,
  Nullifier,
  ResolvedContract,
  PublicDataWrite,
  NearbyWrite,
  SlotSummary,
  TxSlotActivityData,
  SimilarTx,
  L2ToL1Message,
  PrivateLogDetail,
  ContractClassLogDetail,
  PublicLogDetail,
  PublicAddress,
  FeePayerStat,
  PrivacySet,
  FeePricingData,
  TxDetail,
  MurderBoardTx,
  MurderBoardCluster,
  MurderBoardFpc,
  PrivacyScoreFactor,
  MurderBoardData,
  AddressInteraction,
  AddressInteractionsResponse,
  AddressAppearance,
  RepeatedSlot,
  RepeatedSlotsResponse,
  CoTxsResponse,
  CoTxsRow,
  FeatureWeights,
  NormalizationMode,
  AnalysisConfig,
  FeatureStat,
  FeatureStatsData,
  FeeHistoryPoint,
  FeeSpreadBucket,
  CurrentFees,
  ManaStatsResponse,
  BlockHistoryPoint,
  BlockStatsData,
  BlockConfigData,
  ChainReorg,
  CheckpointStatsData,
  ChainHealth,
  ProductionStatsResponse,
  BlockDetail,
  TxCompareResponse,
  SearchResponse,
  SearchInterpretation,
  SearchAddressRole,
  SearchQueryType,
};

const BASE = "/api";

// The API's error convention: every non-2xx response carries {error: string}.
// Surface it as an ApiError so callers can show the server's message instead
// of a generic "API error: 404".
async function throwApiError(res: Response): Promise<never> {
  let detail = null;
  try {
    detail = await res.json();
  } catch {
    /* non-JSON body (proxy error page, …) — fall back to status text */
  }
  throw new ApiError(res.status, res.statusText, detail);
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Centralized 401 handling for any auth-required request. The persisted token
// in localStorage outlives the JWT (24h server-side expiry, JWT_SECRET rotation
// across deploys, etc.) — when the server rejects it, drop it locally so the
// admin UI flips back to the login screen instead of showing an interactive
// page whose actions silently fail.
function handleUnauthorized() {
  const { token, setToken } = useAuthStore.getState();
  if (token) setToken(null);
}

export class ApiError extends Error {
  detail: { error?: string; stderr?: string; stdout?: string } | null;
  constructor(status: number, statusText: string, detail: ApiError["detail"]) {
    super(detail?.error ?? `API error: ${status} ${statusText}`);
    this.detail = detail;
  }
}

async function postJsonAuth<T>(path: string, body?: unknown): Promise<T> {
  // Only declare a JSON body type when we actually have one — fastify
  // otherwise rejects the empty body with "Body cannot be empty when
  // content-type is set to 'application/json'".
  const headers: Record<string, string> = { ...authHeaders() };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function fetchJsonAuth<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders() });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function deleteJsonAuth(path: string): Promise<void>;
async function deleteJsonAuth<T>(path: string, expectJson: true): Promise<T>;
async function deleteJsonAuth<T>(path: string, expectJson?: boolean): Promise<T | void> {
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) await throwApiError(res);
  if (expectJson) return res.json() as Promise<T>;
}

export const FEATURE_NAMES = [
  "numNoteHashes",
  "numNullifiers",
  "numL2ToL1Msgs",
  "numPrivateLogs",
  "numContractClassLogs",
  "numPublicLogs",
  "gasLimitDa",
  "gasLimitL2",
  "maxFeePerDaGas",
  "maxFeePerL2Gas",
  "numSetupCalls",
  "numAppCalls",
  "hasTeardown",
  "totalPublicCalldataSize",
  "expirationDelta",
  "feePayer",
] as const;

export const DEFAULT_WEIGHTS: FeatureWeights = Object.fromEntries(
  FEATURE_NAMES.map((name) => [
    name,
    (name === "maxFeePerDaGas" || name === "maxFeePerL2Gas") ? 0.25 : 1.0,
  ]),
);

export const FEATURE_LABELS: Record<string, { label: string; group: string }> = {
  numNoteHashes: { label: "Note hashes", group: "Shape" },
  numNullifiers: { label: "Nullifiers", group: "Shape" },
  numL2ToL1Msgs: { label: "L2\u21921 msgs", group: "Shape" },
  numPrivateLogs: { label: "Private logs", group: "Shape" },
  numContractClassLogs: { label: "Class logs", group: "Shape" },
  numPublicLogs: { label: "Public logs", group: "Shape" },
  gasLimitDa: { label: "DA mana limit", group: "Mana" },
  gasLimitL2: { label: "L2 mana limit", group: "Mana" },
  maxFeePerDaGas: { label: "Max fee/DA mana", group: "Fees" },
  maxFeePerL2Gas: { label: "Max fee/L2 mana", group: "Fees" },
  numSetupCalls: { label: "Setup calls", group: "Calls" },
  numAppCalls: { label: "App calls", group: "Calls" },
  hasTeardown: { label: "Has teardown", group: "Calls" },
  totalPublicCalldataSize: { label: "Calldata size", group: "Calls" },
  expirationDelta: { label: "Expiration delta", group: "Timing" },
  feePayer: { label: "Fee payer", group: "Identity" },
};

// ── API functions ──

export const api = {
  getNetworks: () => fetchJson<Network[]>("/networks"),
  getNetworkStats: (id: string) => fetchJson<NetworkStats>(`/networks/${id}/stats`),
  getTxs: (
    id: string,
    page = 1,
    limit = 50,
    // Flat query params: status/feePayer/sort/order plus the min*/max* range
    // params the filter panel produces (see parseTxFilters/toApiQuery).
    filters?: Record<string, string>,
  ) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit), ...filters });
    return fetchJson<{ data: Transaction[]; page: number; limit: number; total: number }>(
      `/networks/${id}/txs?${params.toString()}`
    );
  },
  getTxFacets: (id: string, filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters).toString();
    return fetchJson<TxFacetsResponse>(`/networks/${id}/txs/facets${params ? `?${params}` : ""}`);
  },
  getTxStats: (id: string, filters?: Record<string, string>) =>
    fetchJson<TxFilterStatsResponse>(`/networks/${id}/txs/stats?${new URLSearchParams(filters).toString()}`),
  getTxInsights: (id: string, filters?: Record<string, string>) =>
    fetchJson<TxInsightsResponse>(`/networks/${id}/txs/insights?${new URLSearchParams(filters).toString()}`),
  getTxDetail: (id: string, hash: string) =>
    fetchJson<TxDetail>(`/networks/${id}/txs/${hash}`),
  getClusterRuns: (id: string) => fetchJson<ClusterRun[]>(`/networks/${id}/clusters`),
  getClusterDetail: (id: string, runId: number) =>
    fetchJson<{ run: ClusterRun; clusterSizes: ClusterSize[] }>(`/networks/${id}/clusters/${runId}`),
  getUmapData: (id: string, runId: number) =>
    fetchJson<UmapData>(`/networks/${id}/clusters/${runId}/umap`),
  getOutliers: (id: string, runId: number, limit = 50) =>
    fetchJson<{ runId: number; totalTxsAnalyzed: number; outliers: OutlierEntry[] }>(`/networks/${id}/clusters/${runId}/outliers?limit=${limit}`),
  getLabels: (id: string) => fetchJson<ContractLabel[]>(`/networks/${id}/labels`),
  addLabel: (id: string, data: { address: string; label: string; contractType?: string }) =>
    postJsonAuth<ContractLabel>(`/networks/${id}/labels`, data),
  deleteLabel: (id: string, labelId: number) => deleteJsonAuth(`/networks/${id}/labels/${labelId}`),
  getAnalysisStatus: (networkId: string) =>
    fetchJson<{ scheduled: boolean; intervalMinutes: number; running: boolean; config: AnalysisConfig }>(`/networks/${networkId}/analyze/status`),
  login: (password: string) => postJson<{ token: string }>("/auth/login", { password }),
  triggerAnalysis: (
    networkId: string,
    params: Partial<AnalysisConfig>,
  ) => postJsonAuth<{ status: string; output: string }>(`/networks/${networkId}/analyze/trigger`, params),
  saveAnalysisConfig: (
    networkId: string,
    params: Partial<AnalysisConfig>,
  ) => postJsonAuth<{ config: AnalysisConfig }>(`/networks/${networkId}/analyze/config`, params),
  revertAnalysisConfig: (networkId: string) =>
    deleteJsonAuth<{ config: AnalysisConfig }>(`/networks/${networkId}/analyze/config`, true),
  getFeatureStats: (networkId: string) =>
    fetchJson<FeatureStatsData>(`/networks/${networkId}/analyze/feature-stats`),
  getTxSlotActivity: (id: string, hash: string) =>
    fetchJson<TxSlotActivityData>(`/networks/${id}/txs/${hash}/slot-activity`),
  getClusterMembers: (id: string, runId: number, clusterId: number) =>
    fetchJson<{ clusterId: number; members: ClusterMember[] }>(`/networks/${id}/clusters/${runId}/${clusterId}`),
  getFeePayerStats: (id: string) =>
    fetchJson<{ feePayers: FeePayerStat[] }>(`/networks/${id}/txs/stats/fee-payers`),
  getMurderBoard: (id: string, address: string, opts?: { page?: number; limit?: number; clusterId?: number | null }) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set("page", String(opts.page));
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.clusterId !== undefined) params.set("clusterId", String(opts.clusterId));
    const qs = params.toString();
    return fetchJson<MurderBoardData>(`/networks/${id}/murder-board/${encodeURIComponent(address)}${qs ? `?${qs}` : ""}`);
  },
  getAddressInteractions: (id: string, address: string) =>
    fetchJson<AddressInteractionsResponse>(`/networks/${id}/murder-board/${encodeURIComponent(address)}/address-interactions`),
  getRepeatedSlots: (id: string, address: string) =>
    fetchJson<RepeatedSlotsResponse>(`/networks/${id}/murder-board/${encodeURIComponent(address)}/repeated-slots`),
  getCoTxs: (id: string, address: string, coAddress: string, opts?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set("page", String(opts.page));
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return fetchJson<CoTxsResponse>(
      `/networks/${id}/murder-board/${encodeURIComponent(address)}/co/${encodeURIComponent(coAddress)}${qs ? `?${qs}` : ""}`
    );
  },
  getFeeHistory: (id: string, opts?: { from?: number; to?: number; resolution?: string }) => {
    const params = new URLSearchParams();
    if (opts?.from != null) params.set("from", String(opts.from));
    if (opts?.to != null) params.set("to", String(opts.to));
    if (opts?.resolution) params.set("resolution", opts.resolution);
    return fetchJson<{ data: FeeHistoryPoint[]; bucketSize?: number }>(
      `/networks/${id}/fees/history?${params.toString()}`
    );
  },
  getFeeSpread: (id: string, opts?: { from?: number; to?: number; bucketSize?: number }) => {
    const params = new URLSearchParams();
    if (opts?.from != null) params.set("from", String(opts.from));
    if (opts?.to != null) params.set("to", String(opts.to));
    if (opts?.bucketSize != null) params.set("bucketSize", String(opts.bucketSize));
    return fetchJson<{ data: FeeSpreadBucket[]; bucketSize: number }>(
      `/networks/${id}/fees/spread?${params.toString()}`
    );
  },
  getCurrentFees: (id: string) => fetchJson<CurrentFees>(`/networks/${id}/fees/current`),
  getManaStats: (id: string, opts?: { blocks?: number; from?: number; to?: number }) => {
    const params = new URLSearchParams();
    if (opts?.from != null) params.set("from", String(opts.from));
    if (opts?.to != null) params.set("to", String(opts.to));
    if (opts?.from == null && opts?.to == null && opts?.blocks != null)
      params.set("blocks", String(opts.blocks));
    const qs = params.toString();
    return fetchJson<ManaStatsResponse>(`/networks/${id}/mana/stats${qs ? `?${qs}` : ""}`);
  },
  getBlockHistory: (id: string, opts?: { from?: number; to?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.from != null) params.set("from", String(opts.from));
    if (opts?.to != null) params.set("to", String(opts.to));
    if (opts?.limit != null) params.set("limit", String(opts.limit));
    return fetchJson<{ data: BlockHistoryPoint[] }>(`/networks/${id}/blocks/history?${params.toString()}`);
  },
  getBlockStats: (id: string, opts?: { from?: number; to?: number }) => {
    const params = new URLSearchParams();
    if (opts?.from != null) params.set("from", String(opts.from));
    if (opts?.to != null) params.set("to", String(opts.to));
    return fetchJson<{ data: BlockStatsData | null }>(`/networks/${id}/blocks/stats?${params.toString()}`);
  },
  getBlockConfig: (id: string) => fetchJson<{ data: BlockConfigData | null }>(`/networks/${id}/blocks/config`),
  getReorgs: (id: string, opts?: { fromSlot?: number; toSlot?: number }) =>
    fetchJson<{ data: ChainReorg[]; orphanedSlots: number[] }>(
      `/networks/${id}/reorgs` +
        (opts?.fromSlot != null || opts?.toSlot != null
          ? `?${new URLSearchParams({
              ...(opts.fromSlot != null ? { fromSlot: String(opts.fromSlot) } : {}),
              ...(opts.toSlot != null ? { toSlot: String(opts.toSlot) } : {}),
            })}`
          : ""),
    ),
  getCheckpointStats: (id: string, opts?: { fromBlock?: number; toBlock?: number }) => {
    const params = new URLSearchParams();
    if (opts?.fromBlock != null) params.set("fromBlock", String(opts.fromBlock));
    if (opts?.toBlock != null) params.set("toBlock", String(opts.toBlock));
    const qs = params.toString();
    return fetchJson<{ data: CheckpointStatsData | null }>(`/networks/${id}/checkpoints/stats${qs ? `?${qs}` : ""}`);
  },
  getChainHealth: (id: string, epoch?: number) =>
    fetchJson<ChainHealth>(`/networks/${id}/health${epoch != null ? `?epoch=${epoch}` : ""}`),
  /**
   * Block, checkpoint & slot production with miss attribution. Exactly one window selector is
   * accepted, matching the endpoint contract — passing two is a 400, so callers build one of
   * them, never both.
   */
  getProductionStats: (
    id: string,
    opts: {
      hours?: number;
      epoch?: number;
      from?: number;
      to?: number;
      include?: string[];
      groupBy?: "auto" | "hour" | "day" | "epoch";
      limit?: number;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts.epoch != null) params.set("epoch", String(opts.epoch));
    else if (opts.from != null || opts.to != null) {
      if (opts.from != null) params.set("from", String(opts.from));
      if (opts.to != null) params.set("to", String(opts.to));
    } else if (opts.hours != null) params.set("hours", String(opts.hours));
    if (opts.include?.length) params.set("include", opts.include.join(","));
    if (opts.groupBy) params.set("groupBy", opts.groupBy);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    return fetchJson<ProductionStatsResponse>(`/networks/${id}/production/stats?${params.toString()}`);
  },
  getBlockDetail: (id: string, blockNumber: number) =>
    fetchJson<BlockDetail>(`/networks/${id}/blocks/${blockNumber}`),
  compareTxs: (id: string, txHashes: string[]) =>
    postJson<TxCompareResponse>(`/networks/${id}/txs/compare`, { txHashes }),
  /**
   * Universal search. The raw input goes over the wire untouched — including any
   * `tx:`/`address:`/`block:`/`slot:`/`epoch:`/`checkpoint:` prefix hint, which the
   * server strips and uses to narrow which probes run.
   *
   * `limit` caps every match list. Omitting it takes the server's default of 10, which is
   * what the quick-jump dropdown wants; the results page asks for more.
   */
  getSearch: (id: string, q: string, limit?: number) =>
    fetchJson<SearchResponse>(
      `/networks/${id}/search?q=${encodeURIComponent(q)}${limit != null ? `&limit=${limit}` : ""}`
    ),
  getStorageOverview: () =>
    fetchJsonAuth<StorageOverviewData>("/admin/storage"),
  confirmNetworkReset: (id: string) =>
    postJsonAuth<{ networkId: string; rollupAddress: string }>(`/admin/networks/${id}/reset`),
};

export type { StorageOverviewData };
